
import {defineComponent, PropType, ref} from "vue";
import Password from "primevue/password";
import Message from 'primevue/message';
import {
	atLeastOneNumberRegex,
	atLeastOneSpecialCharacterRegex,
	minOneLowerAndUpperCaseRegex,
	passwordLengthRegex
} from "@/util/ValidationHelper";
import {useI18n} from "vue-i18n";

export default defineComponent({
	name: "ResetPasswordForm",
	props: {
		loading: {
			type: Boolean as PropType<boolean>,
			default: false,
		},
	},
	components: {
		Password, Message,
	},
	emit: ['onClickSend'],
	setup(props, {emit}) {
		const {t} = useI18n();

		/** Password of the user */
		const password = ref('');

		/** Repeat of password */
		const passwordRepeat = ref('');

		/** Should the error be shown? */
		const showErrorEqual = ref(false);

		/** Should the error message be shown? */
		const showMessage = ref(false);

		/** Should the error message for mandatory fields be shown? */
		const showErrorMandatory = ref(false);

		/** Should the error message for password length be shown? */
		const showErrorPasswordLength = ref(false);

		/** Should the error message for at least one upper and lower case letter be shown? */
		const showErrorMinOneLowerAndUpperCase = ref(false);

		/** Should the error message for at least one number be shown? */
		const showErrorAtLeastOneNumber = ref(false);

		/** Should the error message for at least one special character be shown? */
		const showErrorOneSpecialCharacter = ref(false);

		/** String with all password validation rules */
		const passwordValidationRules = t('Squeeze.Login.PasswordMustInclude') +
			' \n - ' + t('Forms.Val.PasswordLength') +
			' \n - ' + t('Forms.Val.MinOneLowerAndUpperCase', { field: t('Squeeze.Login.Password')}) +
			' \n - ' + t('Forms.Val.AtLeastOneNumber', { field: t('Squeeze.Login.Password')}) +
			' \n - ' + t('Forms.Val.AtLeastOneSpecialCharacter', { field: t('Squeeze.Login.Password')});

		/** Checks if the two passwords are valid */
		const arePasswordsValid = () => {
			showErrorEqual.value = false;
			showErrorMandatory.value = false;
			showErrorPasswordLength.value = false;
			showErrorMinOneLowerAndUpperCase.value = false;
			showErrorAtLeastOneNumber.value = false;
			showErrorOneSpecialCharacter.value = false;

			if (password.value.length === 0 || passwordRepeat.value.length === 0) {
				showErrorMandatory.value = true;
				return false;
			}

			if (!passwordLengthRegex.test(password.value)) {
				showErrorPasswordLength.value = true;
				return false;
			}

			if (!minOneLowerAndUpperCaseRegex.test(password.value)) {
				showErrorMinOneLowerAndUpperCase.value = true;
				return false;
			}

			if (!atLeastOneNumberRegex.test(password.value)) {
				showErrorAtLeastOneNumber.value = true;
				return false;
			}

			if (!atLeastOneSpecialCharacterRegex.test(password.value)) {
				showErrorOneSpecialCharacter.value = true;
				return false;
			}

			if (password.value !== passwordRepeat.value) {
				showErrorEqual.value = true;
				return false;
			}

			return true;
		}

		/** Triggered on change of password fields */
		const onChangeField = () => {
			if (showMessage.value && arePasswordsValid()) {
				showErrorEqual.value = false;
			}
		}

		/** Triggered on click of send */
		const onClickSend = () => {
			if (!arePasswordsValid()) {
				showMessage.value = true;
				return;
			}

			emit('onClickSend', password.value);
		}

		/**
		 * Triggered on keypress
		 * @param event
		 */
		const onKeyDown = (event: {key: string}) => {
			if (event.key === "Enter") {
				onClickSend();
			}
		}

		return {
			password,
			passwordRepeat,
			showErrorEqual,
			showErrorMandatory,
			showErrorPasswordLength,
			showErrorMinOneLowerAndUpperCase,
			showErrorAtLeastOneNumber,
			showErrorOneSpecialCharacter,
			passwordValidationRules,
			onChangeField,
			onClickSend,
			onKeyDown,
			arePasswordsValid,
		};
	},
})
